import React, { useState } from "react";
import { Star, X, ExternalLink, Phone, Mail, MapPin, CalendarDays, Mic2, HardHat, NotepadText } from "lucide-react";
import { Dialog } from "@headlessui/react";
import { Link } from "react-router-dom";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { getPaymentPlanDetails } from "../../utils/paymentPlanUtils";
import { useToast } from "../../context/ToastContext";
import bookedStamp from "../../assets/booked.png";
import cancelledStampImage from "../../assets/cancelledStampImage.png";

const ProposalDetailedModal = ({ 
  isOpen, 
  onClose, 
  proposal, 
  isProfessionalView = false,
  onEditRequest,
  onAccept,
  onWithdraw,
  onBookNow,
  hostName,
  hostAvatar,
  hostRating,
  hostReviews,
  hideBookNow = false,
}) => {
  const { showSuccess } = useToast();
  const [activeContactPopover, setActiveContactPopover] = useState(null);

  // managerId and portfolio are always set by the backend for customer-view proposals.
  const manager = proposal?.managerId || {};
  const portfolioId = proposal?.portfolioId || manager?.portfolio?._id || null;

  if (!isOpen || !proposal) return null;

  const statusStr = String(proposal?.status || "").toLowerCase();
  const lifecycleStr = String(
    proposal?.lifecycleStatus ||
    proposal?.bookingDoc?.lifecycleStatus ||
    (typeof proposal?.bookingId === "object" ? proposal?.bookingId?.lifecycleStatus : "") ||
    ""
  ).toUpperCase();
  const bookingStatusStr = String(
    proposal?.bookingStatus ||
    proposal?.bookingDoc?.bookingStatus ||
    (typeof proposal?.bookingId === "object" ? proposal?.bookingId?.bookingStatus : "") ||
    ""
  ).toUpperCase();

  const isWithdrawn = statusStr === "withdrawn";
  const isRejected = statusStr === "rejected" || proposal?.status === "rejected";
  const isCancelled = !isRejected && !isWithdrawn && Boolean(
    proposal?.isCancelled ||
    statusStr === "cancelled" ||
    lifecycleStr === "CANCELLED" ||
    bookingStatusStr === "CANCELLED"
  );
  const isBooked = Boolean(
    !isCancelled &&
    !isWithdrawn &&
    !isRejected &&
    (
      proposal?.isBooked ||
      statusStr === "booked" ||
      (Boolean(proposal?.bookingId || proposal?.bookingDoc) && lifecycleStr !== "CANCELLED")
    )
  );

  const name = capitalizeFirstLetter(hostName || proposal.companyName || manager.fullName || "Elegant Events");
  const ratingValue = parseFloat(hostRating ?? proposal.avgRating ?? manager.ratings ?? 0);
  const reviewCount = hostReviews ?? proposal.totalReviews ?? manager.reviews ?? 0;
  const avatar = hostAvatar || proposal.profilePhoto || null;

  // Role — set by backend as proposal.professionalRole from proposal.roleType || prof.role
  const rawRole = (proposal.professionalRole || "planner").toLowerCase();
  const roleDisplay = capitalizeFirstLetter(rawRole);
  const RoleIcon = rawRole === "crew" ? HardHat : rawRole === "performer" ? Mic2 : NotepadText;

  // Location — set by backend as managerId.portfolio.locationText = userCity.name (resolved city string)
  const displayLocation = manager.portfolio?.locationText || "";

  // Year — set by backend as managerId.portfolio.establishmentYear
  const sinceYear = manager.portfolio?.establishmentYear || null;

  const formatBudget = () => {
    const source = proposal.quoteAmount != null ? proposal.quoteAmount : proposal.quote;
    if (source == null) return "N/A";
    let amount = typeof source === "object" ? source.amount : source;
    if (typeof amount !== "number") return "Invalid budget";
    return amount.toLocaleString("en-IN");
  };

  const { label: planLabel, icon: planIcon } = getPaymentPlanDetails(proposal.plan || proposal.paymentPlan);

  const detail = proposal.proposalDetail || proposal.proposalDescription || "We are delighted to present a complete event management solution...";
  const additionalNotes = proposal.additionalDetails || proposal.additionalNotes || "No additional notes.";

  const contactPhone = proposal.contactPhone || proposal.customerPhone || manager.phone || proposal.phone || "+91 9876543210";
  const contactEmail = proposal.contactEmail || proposal.customerEmail || manager.email || proposal.email || "abc@gmail.com";

  const handleCallClick = (e) => {
    e.stopPropagation();
    if (contactPhone && contactPhone !== "N/A") {
      navigator.clipboard.writeText(contactPhone);
      showSuccess("Phone number copied to clipboard!");
    }
    setActiveContactPopover((prev) => (prev === "call" ? null : "call"));
  };

  const handleMailClick = (e) => {
    e.stopPropagation();
    if (contactEmail && contactEmail !== "N/A") {
      navigator.clipboard.writeText(contactEmail);
      showSuccess("Email address copied to clipboard!");
    }
    setActiveContactPopover((prev) => (prev === "mail" ? null : "mail"));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px]" />
      <div className="relative bg-white w-full max-w-[620px] rounded-[24px] p-4 sm:p-5 md:p-6 shadow-2xl max-h-[95vh] overflow-y-auto hide-scrollbar">
        <button
          onClick={onClose}
          className="absolute top-2.5 right-2.5 text-gray-400 hover:text-gray-600 transition-colors z-[60]"
        >
          <X className="w-6 h-6" />
        </button>

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-6 mb-4 sm:mb-5 mt-4 sm:mt-1">
          {/* Left: Host Info */}
          <div className="flex items-center sm:items-start gap-2 sm:gap-4 min-w-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 flex items-center justify-center shrink-0 border border-gray-200 overflow-hidden">
              {avatar ? (
                <img src={avatar} alt={name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[#FF4D12] font-bold text-xl sm:text-2xl">{name.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <h2 className="text-[17px] sm:text-[22px] font-bold text-[#1A1A1A] leading-tight flex items-center gap-1.5 truncate">
                <span className="truncate">{name}</span> 
                {!isProfessionalView && portfolioId && (
                  <Link to={`/profile/${portfolioId}`} target="_blank" rel="noreferrer" className="text-[#FF4D12] hover:text-[#e64510] shrink-0">
                    <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                  </Link>
                )}
              </h2>

              {/* Subtitle Badges Strip: Role, Location, Year — only shown in customer view where backend populates managerId */}
              {!isProfessionalView && (
                <div className="inline-flex items-center flex-nowrap w-fit max-w-full py-0.5 sm:py-1 px-1.5 sm:px-2.5 gap-1 sm:gap-2.5 text-[9.5px] sm:text-[12px] text-gray-500 mt-1 border border-[#0000001A] rounded-md overflow-hidden shrink-0">
                  <span
                    className="flex items-center gap-0.5 sm:gap-1 capitalize shrink-0 font-medium whitespace-nowrap"
                    title={roleDisplay}
                  >
                    <RoleIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-orange-500 shrink-0" />
                    <span>{roleDisplay}</span>
                  </span>
                  {displayLocation && (
                    <span
                      className="flex items-center gap-0.5 sm:gap-1 pl-1 sm:pl-1.5 border-l border-[#0000001A] shrink min-w-0 font-medium"
                      title={displayLocation}
                    >
                      <MapPin className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-orange-500 shrink-0" />
                      <span className="truncate max-w-[65px] sm:max-w-[130px] whitespace-nowrap">{displayLocation}</span>
                    </span>
                  )}
                  {sinceYear && (
                    <span
                      className="flex items-center gap-0.5 sm:gap-1 pl-1 sm:pl-1.5 border-l border-[#0000001A] shrink-0 font-medium whitespace-nowrap"
                      title={`Since ${sinceYear}`}
                    >
                      <CalendarDays className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-orange-500 shrink-0" />
                      <span>Since {sinceYear}</span>
                    </span>
                  )}
                </div>
              )}

              {isWithdrawn && (
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                  Withdrawn
                </span>
              )}
              {reviewCount > 0 && ratingValue > 0 && (
                <div className="flex items-center gap-1 sm:gap-1.5 mt-1">
                  <div className="flex shrink-0">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${star <= ratingValue ? 'fill-[#FF4D12] text-[#FF4D12]' : 'fill-gray-200 text-gray-200'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-[11px] sm:text-[13px] text-gray-600 font-medium truncate">({ratingValue.toFixed(1)}) · {reviewCount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Payment Plan Card */}
          <div className="bg-[#FF4D12] rounded-[10px] p-2.5 sm:p-3 shadow-[0_4px_15px_rgba(0,0,0,0.08)] text-white w-[130px] sm:w-auto sm:min-w-[130px] shrink-0 flex flex-col justify-center text-left ml-0 sm:ml-auto">
            <div className="flex items-center justify-start gap-1 sm:gap-1.5 font-bold text-[11px] sm:text-[12.5px] mb-1 sm:mb-1.5 text-left">
              <img src={planIcon} alt={planLabel} className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain shrink-0" />
              <span className="truncate text-left">{planLabel}</span>
            </div>
            <div className="h-px bg-white/40 w-full mb-1 sm:mb-1.5"></div>
            <div className="flex items-baseline justify-between px-0.5 sm:px-1">
              <span className="text-[14px] sm:text-[18px] font-bold tracking-tight truncate mr-1 text-left">₹{formatBudget()}</span>
              <span className="text-[10px] sm:text-[11px] font-medium opacity-100 shrink-0">Quote</span>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="space-y-4 text-[#1A1A1A]">
          {/* Proposal Detail */}
          <div>
            <h3 className="text-[17px] font-bold mb-3">Proposal Details</h3>
            <div className="border border-gray-200 rounded-[12px] p-4 text-[14px] leading-relaxed text-gray-700 bg-white max-h-[250px] overflow-y-auto no-scrollbar">
              {detail}
            </div>
          </div>

          {/* Additional Notes */}
          {additionalNotes && (
            <div>
              <h3 className="text-[17px] font-bold mb-3">Additional Notes</h3>
              <div className="border border-gray-200 rounded-[12px] p-4 text-[14px] leading-relaxed text-gray-700 bg-white max-h-[160px] overflow-y-auto no-scrollbar">
                {additionalNotes}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER BUTTONS */}
        <div className="mt-3 flex flex-row items-center justify-between gap-3 w-full">
          <div className="flex-1 flex justify-start">
            {isBooked && (
              <img
                src={bookedStamp}
                alt="Booked stamp"
                className="w-24 h-24 sm:w-28 sm:h-28 rotate-[-10deg] opacity-100 drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] pointer-events-none select-none animate-bounce-subtle"
              />
            )}
            {isCancelled && (
              <img
                src={cancelledStampImage}
                alt="Cancelled stamp"
                className="w-24 h-24 sm:w-28 sm:h-28 rotate-[-10deg] opacity-100 drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] pointer-events-none select-none animate-bounce-subtle"
              />
            )}
            {isRejected && (
              <div className="bg-[#FF1717] text-white text-[11px] sm:text-[12px] font-bold px-3 py-1.5 rounded-lg shadow-sm uppercase tracking-wide select-none">
                Rejected
              </div>
            )}
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* If booked: Call & Mail contact buttons with speech bubble popovers */}
            {isBooked && (
              <div className="flex items-center gap-2 mr-1">
                {/* Call Button & Popover */}
                <div className="relative">
                  {activeContactPopover === "call" && (
                    <div className="absolute bottom-full mb-2.5 right-0 sm:right-1/2 sm:translate-x-1/2 bg-[#FFE2D9] text-[#2C2C2C] px-3.5 py-1.5 rounded-xl text-xs sm:text-[13px] font-medium whitespace-nowrap shadow-md z-30 flex items-center gap-1.5">
                      <span>{contactPhone}</span>
                      <div className="absolute top-full right-3.5 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 -mt-1 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#FFE2D9]" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleCallClick}
                    className="w-10 h-10 rounded-xl border border-[#FF5B26] text-[#FF5B26] flex items-center justify-center hover:bg-[#FFF2EC] transition shadow-xs"
                    title="Click to copy phone number"
                  >
                    <Phone className="w-4 h-4 fill-current" />
                  </button>
                </div>

                {/* Mail Button & Popover */}
                <div className="relative">
                  {activeContactPopover === "mail" && (
                    <div className="absolute bottom-full mb-2.5 right-0 bg-[#FFE2D9] text-[#2C2C2C] px-3.5 py-1.5 rounded-xl text-xs sm:text-[13px] font-medium whitespace-nowrap shadow-md z-30 flex items-center gap-1.5">
                      <span>{contactEmail}</span>
                      <div className="absolute top-full right-3.5 -mt-1 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#FFE2D9]" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleMailClick}
                    className="w-10 h-10 rounded-xl border border-[#FF5B26] text-[#FF5B26] flex items-center justify-center hover:bg-[#FFF2EC] transition shadow-xs"
                    title="Click to copy email address"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {!isProfessionalView && !isBooked && !isWithdrawn && !isCancelled && !hideBookNow && (
              <button
                type="button"
                onClick={() => {
                  if (onBookNow) onBookNow(proposal._id);
                }}
                className="px-5 py-2.5 rounded-[12px] bg-[#FF4D12] text-white font-bold text-[14px] sm:text-[15px] hover:bg-[#e64510] shadow-md transition-all active:scale-95"
              >
                Book Now
              </button>
            )}

            {isProfessionalView && proposal.isEditable && !isWithdrawn && !isBooked && (
              <button 
                onClick={() => {
                  onClose();
                  if (onEditRequest) onEditRequest(proposal);
                }}
                className="px-4 sm:px-6 py-2.5 rounded-[6px] bg-[#FF4D12] text-white font-semibold text-[14px] sm:text-[15px] hover:bg-[#e64510] shadow-md transition-colors"
              >
                Edit Proposal
              </button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ProposalDetailedModal;
